import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useDataApi } from "@core/apis";
import type {
  FlexControlRequest,
  Sensor,
  TypedChartDataPoint,
  TypedChartSeries,
} from "@core/apis/shared";
import { useFilteredSensors } from "@core/apis/shared/hooks";
import {
  getFlexControlRequestsDataOptions,
  getFlexStationParametersOptions,
  getStationAdapterConfigOptions,
} from "@core/apis/shared/queries/resource.queries";

import { useGetAverageSensorDataQuery, useGetSensorsDataQuery } from "../customQueryHooks";
import { IndoorTemperatureSeriesType } from "../Optimization/IndoorTemperatureChartContainer";
import { OptimizationSeriesType } from "../Optimization/OptimizationChartContainer";
import { sanitizeSensorValue } from "../sensorUtils";
import type { NonNullableDateRange } from "./useGraphControlState";
import type { RadiatorSensorDataMap } from "./useRadiatorCurveData";

export type RadiatorCurveProps = {
  substationId: string;
  networkUid?: string;
  radiatorSensors?: Sensor[];
  radiatorSensorsLoading: boolean;
  radiatorSensorData?: RadiatorSensorDataMap;
  radiatorSensorDataLoading: boolean;
};

/**
 * Data for the optimization offset chart (measured offset, supply temp, requested offset)
 * and radiator curve section.
 *
 * @param substationId - Substation identifier
 * @param dateRange - Date range
 * @param options.flexStationUid - FlexStation UID for flex control requests.
 *   PropAI: from useGetFlexStations(). Platform: from station.subUid prop.
 * @param options.networkUid - Platform requires this for flex control request queries.
 */
export function useOptimizationOffsetData(
  substationId: string,
  dateRange: NonNullableDateRange,
  options?: { flexStationUid?: string; networkUid?: string }
) {
  const dataApi = useDataApi();

  const { data: radiatorSensors, isLoading: radiatorSensorsLoading } = useFilteredSensors(
    substationId,
    ["radiator_supplytemp_offset", "radiator_system_supply_temp"]
  );

  const { data: flexControlRequestsData } = useQuery({
    ...getFlexControlRequestsDataOptions(
      dataApi,
      options?.flexStationUid,
      dateRange.startDate,
      dateRange.endDate,
      substationId,
      options?.networkUid
    ),
    enabled: !!dataApi && !!options?.flexStationUid,
  });

  const radiatorRelatedSensorUids = useMemo(() => {
    if (!radiatorSensors) return [];
    return radiatorSensors.map((sensor) => sensor.uid);
  }, [radiatorSensors]);

  const sensorDataResult = useGetSensorsDataQuery(substationId, radiatorRelatedSensorUids, {
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const response = sensorDataResult as
    { data?: RadiatorSensorDataMap; isLoading?: boolean } | undefined;
  const radiatorSensorData = response?.data;
  const radiatorSensorDataLoading = response?.isLoading ?? false;

  const optimizationChartData = useMemo(() => {
    const result: TypedChartSeries<OptimizationSeriesType>[] = [];

    if (
      radiatorRelatedSensorUids.length > 0 &&
      (!radiatorSensorData || Object.keys(radiatorSensorData).length === 0)
    ) {
      return result;
    }

    if (!radiatorSensors) return result;

    const measuredOffsetData: TypedChartDataPoint[] = [];
    const supplyTempData: TypedChartDataPoint[] = [];
    const requestedOffsetData: TypedChartDataPoint[] = [];

    radiatorSensors.forEach((sensor) => {
      const uid = sensor.uid;
      const sensorType = sensor.sensor_type_technical_name;
      const sensorDataForUid = radiatorSensorData?.[uid];

      if (!Array.isArray(sensorDataForUid) || sensorDataForUid.length === 0) return;

      const isOffsetSensor =
        sensorType === "radiator_supplytemp_offset" ||
        (sensorType as string) === "radiator_supplytemp_control_offset";
      if (isOffsetSensor) {
        sensorDataForUid.forEach((dp) => {
          const val = sanitizeSensorValue(dp.values.radiator_supplytemp_control_offset);
          if (!Number.isNaN(val) && dp.timestamp) {
            measuredOffsetData.push({ datetime: dp.timestamp, value: val, flags: [] });
          }
        });
      }

      if (sensorType === "radiator_system_supply_temp") {
        sensorDataForUid.forEach((dp) => {
          const val = sanitizeSensorValue(
            dp.values.radiator_system_supply_temp ?? dp.values.supplytemp
          );
          if (!Number.isNaN(val) && dp.timestamp) {
            supplyTempData.push({ datetime: dp.timestamp, value: val, flags: [] });
          }
        });
      }
    });

    flexControlRequestsData?.forEach((request: FlexControlRequest) => {
      requestedOffsetData.push({ datetime: request.timestamp, value: request.values });
    });

    if (measuredOffsetData.length > 0) {
      result.push({ type: OptimizationSeriesType.MEASURED_OFFSET, data: measuredOffsetData });
    }
    if (supplyTempData.length > 0) {
      result.push({ type: OptimizationSeriesType.SUPPLY_TEMP, data: supplyTempData });
    }
    if (requestedOffsetData.length > 0) {
      result.push({ type: OptimizationSeriesType.REQUESTED_OFFSET, data: requestedOffsetData });
    }

    return result;
  }, [radiatorSensorData, radiatorRelatedSensorUids, radiatorSensors, flexControlRequestsData]);

  const radiatorCurveProps: RadiatorCurveProps = {
    substationId,
    networkUid: options?.networkUid,
    radiatorSensors,
    radiatorSensorsLoading,
    radiatorSensorData,
    radiatorSensorDataLoading,
  };

  const isLoading = radiatorSensorsLoading || radiatorSensorDataLoading;
  const isEmpty = !isLoading && optimizationChartData.length === 0;

  return {
    data: { optimizationChartData, radiatorCurveProps },
    isLoading,
    isEmpty,
  };
}

/**
 * Data for the indoor temperature chart on the optimization page
 * (average temperature + target temperature line).
 *
 * @param substationId - Substation identifier
 * @param dateRange - Date range
 * @param options.flexStationUid - FlexStation UID for adapter config + parameters lookup.
 * @param options.networkUid - Platform requires this for flex station queries.
 */
export function useIndoorTemperatureData(
  substationId: string,
  dateRange: NonNullableDateRange,
  options?: { flexStationUid?: string; networkUid?: string }
) {
  const dataApi = useDataApi();

  // Fetch average indoor temperature
  const { data: indoorTempAverageData, isLoading } = useGetAverageSensorDataQuery(
    substationId,
    "living_air_temperature",
    { startDate: dateRange.startDate, endDate: dateRange.endDate }
  );

  // Fetch adapter config for local control check
  const { data: adapterConfig } = useQuery({
    ...getStationAdapterConfigOptions(
      dataApi,
      options?.flexStationUid,
      substationId,
      options?.networkUid
    ),
    enabled: !!dataApi && !!options?.flexStationUid,
  });

  // Fetch flex station parameters for target temperature
  const { data: flexStationParameters } = useQuery({
    ...getFlexStationParametersOptions(
      dataApi,
      options?.flexStationUid ?? null,
      substationId,
      options?.networkUid
    ),
    enabled: !!options?.flexStationUid && !!dataApi,
  });

  const isLocalControlEnabled = adapterConfig?.allow_local_control === true;
  const targetTemperature = flexStationParameters?.indoor_temp?.min;

  const indoorTemperatureChartData = useMemo(() => {
    const result: TypedChartSeries<IndoorTemperatureSeriesType>[] = [];

    if (Array.isArray(indoorTempAverageData) && indoorTempAverageData.length > 0) {
      const averageTempData = indoorTempAverageData
        .map((dataPoint) => ({
          datetime: dataPoint.timestamp,
          value: sanitizeSensorValue(dataPoint.values?.living_air_temperature),
          flags: [] as string[],
        }))
        .filter((dataPoint) => dataPoint.datetime && !Number.isNaN(dataPoint.value));

      if (averageTempData.length > 0) {
        result.push({
          type: IndoorTemperatureSeriesType.AVERAGE_TEMPERATURE,
          data: averageTempData,
        });
      }
    }

    if (isLocalControlEnabled && typeof targetTemperature === "number") {
      const startDatetime = dateRange.startDate.toISO();
      const endDatetime = dateRange.endDate.toISO();

      if (startDatetime && endDatetime) {
        result.push({
          type: IndoorTemperatureSeriesType.TARGET_TEMPERATURE,
          data: [
            { datetime: startDatetime, value: targetTemperature, flags: [] },
            { datetime: endDatetime, value: targetTemperature, flags: [] },
          ],
        });
      }
    }

    return result;
  }, [indoorTempAverageData, isLocalControlEnabled, targetTemperature, dateRange]);

  const isEmpty = !isLoading && indoorTemperatureChartData.length === 0;

  return {
    data: { indoorTemperatureChartData },
    isLoading,
    isEmpty,
  };
}
